import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  styled,
  IconButton,
  DialogActions,
  createTheme,
  alpha,
  useMediaQuery,
  Zoom,
  Fab,
} from "@mui/material";
import {
  Calendar,
  Briefcase,
  GraduationCap,
  User,
  MapPin,
  Phone,
  Building,
  Award,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Close } from "@mui/icons-material";
import CandidateProfile from "./CandidateHistory";
import ApplicationSearchBar from "./ApplicationSearchBar";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import colors from "colors/color";

// Import API URL from environment variables
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#0017E7',
    },
    secondary: {
      main: '#D9D9D9',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#0017E7', 0.05),
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha('#0017E7', 0.04),
          },
        },
      },
    },
  },
});

// Define a common style for all Show More buttons
const showMoreInlineSx = {
  textTransform: "none !important",
  color: "#0017E7",
  fontWeight: 500,
  fontSize: "0.875rem",
  '&:hover': {
    backgroundColor: "rgba(0, 23, 231, 0.08)",
    color: "#0017E7"
  }
};

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: '0 4px 20px rgba(0, 23, 231, 0.05)',
  borderRadius: 12,
  margin: theme.spacing(2, 0),
  overflow: 'hidden',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
  padding: theme.spacing(2),
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
  padding: theme.spacing(2),
  color: '#0017E7',
  fontWeight: 600,
  fontSize: '0.95rem',
  '& .MuiBox-root': {
    color: '#0017E7',
  },
  '& svg': {
    color: '#0017E7',
  }
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 500,
  borderRadius: "12px",
  backgroundColor: "rgba(0, 23, 231, 0.08)",
  color: "#0017E7",
  border: "1px solid rgba(0, 23, 231, 0.15)",
  boxShadow: "0 2px 5px rgba(0, 23, 231, 0.1)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#0017E7",
    color: "#ffffff",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0, 23, 231, 0.25)",
  },
  "& .MuiChip-label": {
    padding: "8px 14px",
    fontSize: "1rem",
    fontWeight: 500,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  "& .MuiChip-icon": {
    color: "#0017E7",
  },
}));

const ViewToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 24px",
  fontSize: "0.95rem",
  background: "linear-gradient(135deg, #0017E7 0%, rgba(0, 23, 231, 0.8) 100%)",
  color: "#ffffff",
  border: "none",
  boxShadow: "0 6px 20px rgba(0, 23, 231, 0.25)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, rgba(0, 23, 231, 0.9) 0%, #0017E7 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(0, 23, 231, 0.35)",
  }
}));

const ScrollableTableContainer = styled(TableContainer)(({ theme, ismobile }) => ({
  boxShadow: '0 4px 20px rgba(0, 23, 231, 0.05)',
  borderRadius: 12,
  margin: theme.spacing(2, 0),
  overflowX: ismobile ? 'auto' : 'hidden',
  width: '100%',
  '&::-webkit-scrollbar': {
    height: ismobile ? '8px' : 0,
  },
  '&::-webkit-scrollbar-track': {
    background: ismobile ? 'rgba(0, 23, 231, 0.05)' : 'transparent',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: ismobile ? 'rgba(0, 23, 231, 0.2)' : 'transparent',
    borderRadius: '4px',
    '&:hover': {
      background: ismobile ? 'rgba(0, 23, 231, 0.3)' : 'transparent',
    },
  },
}));

const MobileTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    '& .MuiTypography-root': {
      fontSize: '0.875rem',
    },
    '& .MuiChip-root': {
      height: '24px',
      '& .MuiChip-label': {
        fontSize: '0.75rem',
        padding: '0 8px',
      },
    },
  },
}));

// Add new styled components for mobile responsiveness
const MobileScrollButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  backgroundColor: '#0017E7',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: 'rgba(0, 23, 231, 0.9)',
  },
  zIndex: 1000,
}));

// Update TruncatedContent component
const TruncatedContent = ({ content, maxLength, openDialog }) => {
  if (!content) return "-";
  
  const truncated = content.length > maxLength 
    ? `${content.substring(0, maxLength)}...` 
    : content;

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      gap: 0.5
    }}>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontSize: "1rem",
        }}
      >
        {truncated}
      </Typography>
      {content.length > maxLength && (
        <Button
          variant="text"
          color="primary"
          onClick={() => openDialog("Full Content", content)}
          size="small"
          sx={{
            ...showMoreInlineSx,
            alignSelf: 'flex-start',
            mt: 0.5
          }}
        >
          See More
        </Button>
      )}
    </Box>
  );
};

// Function to truncate experience text
const truncateExperienceText = (text) => {
  if (!text) return "";
  return text.length > 30 ? `${text.substring(0, 30)}...` : text;
};

// Helper function to parse string to array
const parseStringToArray = (data) => {
  if (!data) return [];

  // If it's already an array, just return it
  if (Array.isArray(data)) return data;

  // If it's a string, try to clean and parse it
  if (typeof data === "string") {
    // First, check if it's a JSON string
    if (data.startsWith("[") && data.endsWith("]")) {
      try {
        // Try standard JSON parsing first
        return JSON.parse(data);
      } catch (e) {
        // If that fails, try to clean up the format and parse again
        const cleanedString = data
          .replace(/\[|\]|'|"/g, "") // Remove brackets, single and double quotes
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean); // Remove empty strings

        return cleanedString;
      }
    }

    // If it's not a JSON-like string, just split by commas
    if (data.includes(",")) {
      return data
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    // If it's just a single value, return as an array with one item
    return [data];
  }

  // If we can't parse it, return an empty array
  return [];
};

// Clean a string by removing quotes and brackets
const cleanString = (str) => {
  if (!str) return "";
  return str.replace(/^\[|\]$|'|"/g, "").trim();
};

const ApplicationTable2 = ({
  data,
  tableType = "applications",
  viewType,
  setViewType,
  initialTotalCount = null,
  onTotalCountChange = null, // Add callback for total count changes
  onDataChange = null, // Add callback for data changes
}) => {
  // State to manage the single dialog for content display
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [scale, setScale] = useState(1);

  // State for candidate profile dialog
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // State for filtered data - initialize with the data prop
  const [filteredData, setFilteredData] = useState([]);

  // Add new state for experience dialog
  const [moreItemsDialogOpen, setMoreItemsDialogOpen] = useState(false);
  const [moreItemsDialogTitle, setMoreItemsDialogTitle] = useState("");
  const [moreItemsDialogContent, setMoreItemsDialogContent] = useState([]);

  const isVerySmallScreen = useMediaQuery("(max-width:800px)");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const tableRef = React.useRef(null);

  const renderLimitedItems = (items, title, limit = 1, charLimit = 4) => {
    if (!items || items.length === 0) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontSize: "1.1rem" }}
        >
          No {title.toLowerCase()} listed
        </Typography>
      );
    }

    // Helper function to check if any item needs truncation
    const needsTruncation = (item) => cleanString(item).length > charLimit;
    const hasLongContent = items.some(needsTruncation);

    return (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexDirection: { xs: "column", xl: "row" },
            alignItems: { xs: "flex-start", xl: "center" },
            border: `1px solid ${colors.primary}`,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {items.slice(0, limit).map((item, i) => {
              const cleanedItem = cleanString(item);
              const truncatedItem =
                cleanedItem.length > charLimit
                  ? `${cleanedItem.substring(0, charLimit)}...`
                  : cleanedItem;

              return (
                <InfoChip
                  key={i}
                  label={truncatedItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowMore(title, items);
                  }}
                />
              );
            })}
          </Box>

          {(items.length > limit || hasLongContent) && (
            <Button
              variant="text"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleShowMore(title, items);
              }}
              size="small"
              sx={showMoreInlineSx}
            >
              {items.length > limit
                ? `Show ${items.length - limit} more`
                : "Show full content"}
            </Button>
          )}
        </Box>
      </>
    );
  };

  // Scaling logic (from ApplicationTable.jsx)
  useEffect(() => {
    if (!isVerySmallScreen) {
      const handleResize = () => {
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const contentHeight = 800;
        const contentWidth = 1200;
        const heightScale = (windowHeight * 0.95) / contentHeight;
        const widthScale = (windowWidth * 0.95) / contentWidth;
        let newScale = Math.min(heightScale, widthScale, 1);
        if (windowWidth <= 1920) {
          newScale = Math.max(newScale, 0.8);
        } else if (windowWidth <= 2560) {
          newScale = Math.max(newScale, 0.85);
        } else {
          newScale = Math.max(newScale, 0.9);
        }
        setScale(newScale);
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    } else {
      setScale(1);
    }
  }, [isVerySmallScreen]);

  // Update filteredData when data prop changes
  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Update your openDialog function to clean the dialog content
  const openDialog = (title, content) => {
    // Clean the content of brackets and quotes if it's a string
    let cleanedContent = content;
    if (typeof content === "string") {
      // Check if it's an array-like string
      if (content.startsWith("[") && content.endsWith("]")) {
        cleanedContent = content
          .replace(/^\[|\]$/g, "") // Remove outer brackets
          .replace(/\[|\]/g, "") // Remove any inner brackets
          .replace(/"/g, "") // Remove double quotes
          .replace(/'/g, ""); // Remove single quotes
      }
    }

    setDialogTitle(title);
    setDialogContent(cleanedContent);
    setDialogOpen(true);
  };

  // This function opens the candidate profile dialog
  const openCandidateProfile = (candidateId) => {
    console.log("candidateId", candidateId);
    setSelectedCandidateId(candidateId);
    setCandidateDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);
  const closeCandidateDialog = () => setCandidateDialogOpen(false);

  // Handler for search functionality
  const handleSearch = (searchResults, totalCount = null) => {
    setFilteredData(searchResults);
    // Notify parent component of total count change
    if (onTotalCountChange && totalCount !== null) {
      onTotalCountChange(totalCount);
    }
    // Notify parent component of data change
    if (onDataChange) {
      onDataChange(searchResults);
    }
  };

  // Update the handleShowMore function
  const handleShowMore = (title, items) => {
    setMoreItemsDialogTitle(title);

    // For experiences, we know it's already an array of objects with text and date_range
    if (title === "Experience" && Array.isArray(items)) {
      setMoreItemsDialogContent(
        items.map((item) => ({
          type: "experience",
          text: item.text || "",
          date_range: item.date_range || "",
        }))
      );
    } else {
      // For other types of content
      const parsedItems = Array.isArray(items) ? items : [items];
      setMoreItemsDialogContent(
        parsedItems.map((item) => {
          if (typeof item === "string" && item.includes("LinkedIn:")) {
            const linkedInUrl = item.replace("LinkedIn:", "").trim();
            return {
              type: "link",
              label: "LinkedIn Profile",
              href: linkedInUrl.startsWith("http")
                ? linkedInUrl
                : `https://www.linkedin.com/in/${linkedInUrl}`,
            };
          }
          return item;
        })
      );
    }
    setMoreItemsDialogOpen(true);
  };

  // Add scroll button visibility logic
  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tableRef.current;
        setShowScrollButton(scrollLeft < scrollWidth - clientWidth);
      }
    };

    const table = tableRef.current;
    if (table) {
      table.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial check
    }

    return () => {
      if (table) {
        table.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleScroll = (direction) => {
    if (tableRef.current) {
      const scrollAmount = 300; // Adjust this value as needed
      tableRef.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <Box
      sx={
        isVerySmallScreen
          ? { width: "100%", overflow: "hidden", position: "relative" }
          : {
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              width: `${100 / scale}%`,
              height: `${100 / scale}%`,
              transition: "transform 0.3s ease-in-out",
            }
      }
    >
      <Paper
        elevation={0}
        sx={{ borderRadius: 2, overflow: "hidden", width: "100%" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            mb: { xs: 1, sm: 2 },
            p: { xs: 1, sm: 2 },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <ViewToggleButton
            onClick={setViewType}
            startIcon={
              viewType === "card" ? <ViewListIcon /> : <ViewModuleIcon />
            }
            sx={{
              width: { xs: "100%", sm: "auto" },
              mb: { xs: 1, sm: 0 },
            }}
          >
            {viewType === "card" ? "List View" : "Card View"}
          </ViewToggleButton>
          <Box sx={{ flex: 1, width: "100%" }}>
            <ApplicationSearchBar
              data={data}
              onSearch={handleSearch}
              tableType={tableType}
              SEARCH_API_ENDPOINT={`${apiUrl}candidates/api/applications/search/`}
              AUTOCOMPLETE_API_ENDPOINT={`${apiUrl}candidates/api/applications/autocomplete/`}
              location={"location"}
            />
          </Box>
        </Box>

        <ScrollableTableContainer ref={tableRef} ismobile={isVerySmallScreen}>
          <Table
            sx={{
              width: "100%",
              minWidth: 1200,
              tableLayout: "fixed",
            }}
          >
            <TableHead>
              <TableRow>
                <StyledHeaderCell sx={{ width: "15%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <User size={16} />
                    Applicant
                  </Box>
                </StyledHeaderCell>
                <StyledHeaderCell sx={{ width: "10%" }}>
                  Contact
                </StyledHeaderCell>
                <StyledHeaderCell sx={{ width: "20%" }}>Email</StyledHeaderCell>
                <StyledHeaderCell sx={{ width: "15%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MapPin size={16} />
                    Location
                  </Box>
                </StyledHeaderCell>
                <StyledHeaderCell sx={{ width: "15%" }}>
                  Skills
                </StyledHeaderCell>
                <StyledHeaderCell sx={{ width: "15%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <GraduationCap size={16} />
                    Education
                  </Box>
                </StyledHeaderCell>
                <StyledHeaderCell sx={{ width: "15%" }}>
                  Experiences
                </StyledHeaderCell>
                <StyledHeaderCell sx={{ width: "15%" }}>
                  Profile
                </StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": { backgroundColor: alpha("#0017E7", 0.04) },
                    cursor: "pointer",
                  }}
                  onClick={() => openCandidateProfile(row.candidate_id)}
                >
                  <MobileTableCell sx={{ width: "15%" }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1.1rem" },
                          fontWeight: 600,
                        }}
                      >
                        {row.first_name} {row.last_name}
                      </Typography>
                    </Box>
                  </MobileTableCell>
                  <MobileTableCell
                    sx={{ width: "10%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1.1rem" },
                      }}
                    >
                      {row.phone || "-"}
                    </Typography>
                  </MobileTableCell>
                  <MobileTableCell
                    sx={{ width: "20%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1.1rem" },
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {row.email || "-"}
                    </Typography>
                  </MobileTableCell>
                  <MobileTableCell
                    sx={{ width: "15%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1.1rem" },
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {row.city || "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "1rem" },
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {row.address ? (
                          <TruncatedContent
                            content={row.address}
                            maxLength={10}
                            openDialog={(title) =>
                              openDialog("Address", row.address)
                            }
                          />
                        ) : (
                          "-"
                        )}
                      </Typography>
                    </Box>
                  </MobileTableCell>
                  <MobileTableCell
                    sx={{ width: "15%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        maxWidth: "100%",
                      }}
                    >
                      {row.skills ? (
                        <>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {parseStringToArray(row.skills)
                              .slice(0, 1)
                              .map((item, i) => (
                                <InfoChip
                                  key={i}
                                  label={cleanString(item)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowMore(
                                      "Skills",
                                      parseStringToArray(row.skills)
                                    );
                                  }}
                                />
                              ))}
                          </Box>
                          {parseStringToArray(row.skills).length > 1 && (
                            <Button
                              variant="text"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowMore(
                                  "Skills",
                                  parseStringToArray(row.skills)
                                );
                              }}
                              size="small"
                              sx={showMoreInlineSx}
                            >
                              Show {parseStringToArray(row.skills).length - 1}{" "}
                              more
                            </Button>
                          )}
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "1.1rem" },
                          }}
                        >
                          No skills listed
                        </Typography>
                      )}
                    </Box>
                  </MobileTableCell>
                  <MobileTableCell
                    sx={{ width: "15%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderLimitedItems(
                      parseStringToArray(row.educations),
                      "Education",
                      1,
                      4
                    )}
                  </MobileTableCell>
                  <MobileTableCell
                    sx={{ width: "15%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        <InfoChip
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {(row.extracted_informations?.agexperiece || 0) >=
                                1 && (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                    color: "text.secondary",
                                  }}
                                >
                                  {truncateExperienceText("+")}
                                </Typography>
                              )}
                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: { xs: "0.875rem", sm: "1rem" },
                                }}
                              >
                                <strong>
                                  {(row.extracted_informations?.agexperiece ||
                                    0) < 1
                                    ? "Less than 1 year"
                                    : `${
                                        row.extracted_informations
                                          ?.agexperiece || 0
                                      } years`}
                                </strong>
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                      {row.extracted_informations?.EXPERIENCE?.length > 0 && (
                        <Button
                          variant="text"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowMore(
                              "Experience",
                              row.extracted_informations.EXPERIENCE
                            );
                          }}
                          sx={{
                            ...showMoreInlineSx,
                            alignSelf: "flex-start",
                            mt: 0.5,
                          }}
                          size="small"
                        >
                          See More
                        </Button>
                      )}
                    </Box>
                  </MobileTableCell>
                  <MobileTableCell
                    sx={{ width: "15%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        maxWidth: "100%",
                      }}
                    >
                      {row.profile ? (
                        <>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {parseStringToArray(row.profile)
                              .slice(0, 1)
                              .map((item, i) => {
                                const cleanedItem = cleanString(item);
                                const truncatedItem =
                                  cleanedItem.length > 4
                                    ? `${cleanedItem.substring(0, 4)}...`
                                    : cleanedItem;
                                return (
                                  <InfoChip
                                    key={i}
                                    label={truncatedItem}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowMore(
                                        "Profile",
                                        parseStringToArray(row.profile)
                                      );
                                    }}
                                  />
                                );
                              })}
                          </Box>
                          {(parseStringToArray(row.profile).length > 1 ||
                            cleanString(parseStringToArray(row.profile)[0])
                              .length > 4) && (
                            <Button
                              variant="text"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowMore(
                                  "Profile",
                                  parseStringToArray(row.profile)
                                );
                              }}
                              size="small"
                              sx={showMoreInlineSx}
                            >
                              {parseStringToArray(row.profile).length > 1
                                ? `Show ${
                                    parseStringToArray(row.profile).length - 1
                                  } more`
                                : "Show full content"}
                            </Button>
                          )}
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "1.1rem" },
                          }}
                        >
                          No profile listed
                        </Typography>
                      )}
                    </Box>
                  </MobileTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTableContainer>

        {isVerySmallScreen && (
          <Box
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              display: "flex",
              gap: 1,
              zIndex: 1000,
            }}
          >
            <Zoom in={showScrollButton}>
              <MobileScrollButton
                size="small"
                onClick={() => handleScroll("left")}
                sx={{
                  backgroundColor: "rgba(0, 23, 231, 0.8)",
                  "&:hover": { backgroundColor: "rgba(0, 23, 231, 0.9)" },
                }}
              >
                <ChevronLeft size={20} />
              </MobileScrollButton>
            </Zoom>
            <Zoom in={showScrollButton}>
              <MobileScrollButton
                size="small"
                onClick={() => handleScroll("right")}
                sx={{
                  backgroundColor: "rgba(0, 23, 231, 0.8)",
                  "&:hover": { backgroundColor: "rgba(0, 23, 231, 0.9)" },
                }}
              >
                <ChevronRight size={20} />
              </MobileScrollButton>
            </Zoom>
          </Box>
        )}
      </Paper>

      {/* Content Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 23, 231, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.3rem",
            fontWeight: 600,
            color: "#0017E7",
            borderBottom: "1px solid rgba(0, 23, 231, 0.1)",
            pb: 2,
          }}
        >
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              py: 2,
              fontSize: "1.1rem",
              color: "#424242",
            }}
          >
            {typeof dialogContent === 'string' ? dialogContent : JSON.stringify(dialogContent)}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Candidate Profile Dialog */}
      <Dialog
        fullWidth={true}
        maxWidth="md"
        open={candidateDialogOpen}
        onClose={closeCandidateDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 23, 231, 0.15)",
          },
        }}
      >
        {selectedCandidateId && (
          <CandidateProfile candidate_id={selectedCandidateId} />
        )}
      </Dialog>

      {/* "Show More" Dialog */}
      <Dialog
        open={moreItemsDialogOpen}
        onClose={() => setMoreItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 23, 231, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "rgba(0, 23, 231, 0.05)",
            borderBottom: "1px solid rgba(0, 23, 231, 0.1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 600, color: "#0017E7" }}
          >
            {moreItemsDialogTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setMoreItemsDialogOpen(false)}
            sx={{
              color: "#0017E7",
              "&:hover": {
                backgroundColor: "rgba(0, 23, 231, 0.08)",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
          {moreItemsDialogTitle === "Experience" ? (
            <Box sx={{ maxHeight: "70vh", padding: 3 }}>
              {moreItemsDialogContent.map((experience, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2,
                    border: "1px solid rgba(0, 23, 231, 0.1)",
                    boxShadow: "0 2px 4px rgba(0, 23, 231, 0.05)",
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 23, 231, 0.02)" : "#ffffff",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 8px rgba(0, 23, 231, 0.08)",
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      color: "#424242",
                      fontSize: "1rem",
                      fontWeight: 500,
                      lineHeight: 1.6,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {experience.text}
                  </Typography>

                  {experience.date_range && (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        color: "#757575",
                        fontSize: "0.875rem",
                        fontStyle: "italic",
                        mt: 1,
                      }}
                    >
                      {experience.date_range}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 2 }}>
              {moreItemsDialogContent.map((item, i) => {
                if (typeof item === "object" && item.type === "link") {
                  return (
                    <InfoChip
                      key={i}
                      label={item.label}
                      component="a"
                      href={item.href}
                      target="_blank"
                      clickable
                    />
                  );
                }
                return (
                  <InfoChip
                    key={i}
                    label={
                      typeof item === "string" ? item : JSON.stringify(item)
                    }
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: 2,
            borderTop: "1px solid rgba(0, 23, 231, 0.1)",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setMoreItemsDialogOpen(false)}
            color="primary"
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              background:
                "linear-gradient(135deg, #0017E7 0%, rgba(0, 23, 231, 0.8) 100%)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(0, 23, 231, 0.9) 0%, #0017E7 100%)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicationTable2;
